import React, { useState, useEffect, useCallback } from 'react';
import { Camera, Upload, Search, X, Save, User, Calendar, Globe, Phone, Mail } from 'lucide-react';
import api from '../../api';

// Interests will be loaded from backend API

const EnhancedProfileManagement = ({ userId }) => {
  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    birthday: '',
    gender: '',
    about: '',
    profilePictureUrl: '',
    address: {
      state: '',
      city: '',
      pincode: ''
    },
    website: '',
    contactNumber: '',
    email: '',
    isPrivate: false
  });

  const [interests, setInterests] = useState([]);
  const [selectedInterests, setSelectedInterests] = useState([]);
  const [interestSearch, setInterestSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [interestsLoading, setInterestsLoading] = useState(false);
  const [interestsError, setInterestsError] = useState(null);
  const [states, setStates] = useState([]);
  const [statesLoading, setStatesLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [imagePreview, setImagePreview] = useState(null);

  const genderOptions = [
    { value: '', label: 'Select Gender' },
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'non-binary', label: 'Non-binary' },
    { value: 'prefer-not-to-say', label: 'Prefer not to say' }
  ];

  const fetchProfile = useCallback(async () => {
    try {
      const response = await api.get(`/creators/${userId}/profile`);
      const profileData = response.data;
      
      setProfile({
        firstName: profileData.user?.name?.split(' ')[0] || '',
        lastName: profileData.user?.name?.split(' ').slice(1).join(' ') || '',
        birthday: profileData.birthday || '',
        gender: profileData.gender || '',
        about: profileData.bio || '',
        profilePictureUrl: profileData.profilePictureUrl || '',
        address: profileData.address || { state: '', city: '', pincode: '' },
        website: profileData.website || '',
        contactNumber: profileData.contactNumber || '',
        email: profileData.user?.email || '',
        isPrivate: profileData.isPrivate || false
      });

      if (profileData.contentCategories) {
        setSelectedInterests(profileData.contentCategories);
      }

      if (profileData.profilePictureUrl) {
        setImagePreview(profileData.profilePictureUrl);
      }
    } catch (error) {
      // Error handled silently
    }
  }, [userId]);

  useEffect(() => {
    const loadAvailableInterests = async () => {
      setInterestsLoading(true);
      setInterestsError(null);
      try {
        // Fetch interests from backend API
        const response = await api.get('/interests/grouped');
        const interestsData = response.data;

        // Convert grouped interests to the format expected by the component
        const allInterests = [];
        Object.entries(interestsData).forEach(([category, items]) => {
          items.forEach(item => {
            allInterests.push({ category, name: item, id: `${category}-${item}` });
          });
        });
        setInterests(allInterests);
      } catch (error) {
        setInterestsError('Failed to load interests. Please refresh the page or contact support.');
        // Fallback to empty array if API fails
        setInterests([]);
      } finally {
        setInterestsLoading(false);
      }
    };

    if (userId) {
      fetchProfile();
      loadAvailableInterests();
    }
  }, [userId, fetchProfile]);

  // Fetch states from backend
  useEffect(() => {
    const loadStates = async () => {
      setStatesLoading(true);
      try {
        const response = await api.get('/states');

        if (Array.isArray(response.data)) {
          setStates(response.data);
        } else {
          setStates([]);
        }
      } catch (error) {
        setStates([]);
      } finally {
        setStatesLoading(false);
      }
    };

    loadStates();
  }, []);

  const handleInputChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        setLoading(true);
        
        // Create preview immediately for better UX
        const reader = new FileReader();
        reader.onload = (e) => {
          setImagePreview(e.target.result);
        };
        reader.readAsDataURL(file);

        // Upload to S3 via backend
        const formData = new FormData();
        formData.append('image', file);

        const response = await api.post(`/creators/${userId}/profile/upload-image`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        // Update profile with S3 URL
        if (response.data.profilePictureUrl) {
          setProfile(prev => ({ 
            ...prev, 
            profilePictureUrl: response.data.profilePictureUrl 
          }));
          setMessage('Profile image uploaded successfully!');
          setTimeout(() => setMessage(''), 3000);
        }

      } catch (error) {
        setMessage('Failed to upload image. Please try again.');
        // Reset preview on error
        setImagePreview(null);
      } finally {
        setLoading(false);
      }
    }
  };

  const addInterest = (interest) => {
    if (!selectedInterests.includes(interest.name)) {
      setSelectedInterests(prev => [...prev, interest.name]);
    }
    setInterestSearch('');
  };

  const removeInterest = (interestToRemove) => {
    setSelectedInterests(prev => prev.filter(interest => interest !== interestToRemove));
  };

  const filteredInterests = interests.filter(interest => {
    const matchesSearch = interest.name.toLowerCase().includes(interestSearch.toLowerCase());
    const matchesCategory = !selectedCategory || interest.category === selectedCategory;
    const notSelected = !selectedInterests.includes(interest.name);
    return matchesSearch && matchesCategory && notSelected;
  });

  // Get unique categories from interests
  const categories = [...new Set(interests.map(i => i.category))].sort();


  const handleAddressChange = (field, value) => {
    setProfile(prev => ({
      ...prev,
      address: {
        ...prev.address,
        [field]: value
      }
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const updateData = {
        bio: profile.about,
        profilePictureUrl: profile.profilePictureUrl,
        website: profile.website,
        contactNumber: profile.contactNumber,
        address: profile.address,
        contentCategories: selectedInterests,
        gender: profile.gender,
        birthday: profile.birthday,
        isPrivate: profile.isPrivate
      };

      await api.put(`/creators/${userId}/profile`, updateData);
      setMessage('Profile updated successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage('Error updating profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <h2 className="text-2xl font-bold text-gray-900">My Profile</h2>
        <p className="text-gray-600 mt-1">Manage your profile information and preferences</p>
      </div>

      <div className="p-6 space-y-8">
        {/* Avatar & Profile Image */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Avatar & Profile Image</h3>
          <p className="text-sm text-gray-600">A unique avatar and profile header will help brands identify you!</p>
          
          <div className="flex items-center space-x-6">
            <div className="relative">
              <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                {imagePreview || profile.profilePictureUrl ? (
                  <img 
                    src={imagePreview || profile.profilePictureUrl} 
                    alt="Profile" 
                    className="w-full h-full object-cover" 
                  />
                ) : (
                  <User className="w-8 h-8 text-gray-400" />
                )}
                {loading && (
                  <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                  </div>
                )}
              </div>
              <label className="absolute bottom-0 right-0 bg-blue-600 rounded-full p-2 cursor-pointer hover:bg-blue-700 transition-colors">
                <Camera className="w-4 h-4 text-white" />
                <input 
                  type="file" 
                  className="hidden" 
                  accept="image/*" 
                  onChange={handleImageUpload}
                  disabled={loading}
                />
              </label>
            </div>
            
            <div className="flex-1">
              <label className="block w-full h-32 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 transition-colors">
                <Upload className="w-8 h-8 text-gray-400 mb-2" />
                <span className="text-sm text-gray-600">Upload header image</span>
                <input type="file" className="hidden" accept="image/*" />
              </label>
            </div>
          </div>
        </div>

        {/* Details */}
        <div className="space-y-6">
          <h3 className="text-lg font-semibold text-gray-900">Details</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
              <input
                type="text"
                value={profile.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your first name"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
              <input
                type="text"
                value={profile.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your last name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Birthday</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
                <input
                  type="date"
                  value={profile.birthday}
                  onChange={(e) => handleInputChange('birthday', e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
              <select
                value={profile.gender}
                onChange={(e) => handleInputChange('gender', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {genderOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            {/* Address Section */}
            <div className="md:col-span-2">
              <h4 className="text-md font-semibold text-gray-900 mb-2">Address</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* State Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                  <select
                    value={profile.address.state || ''}
                    onChange={e => handleAddressChange('state', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={statesLoading}
                  >
                    <option value="">
                      {statesLoading ? 'Loading states...' : 'Select State'}
                    </option>
                    {states.map((state) => (
                      <option key={state.id} value={state.stateName}>
                        {state.stateName}
                      </option>
                    ))}
                  </select>
                </div>
                {/* City Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                  <input
                    type="text"
                    value={profile.address.city}
                    onChange={e => handleAddressChange('city', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter city"
                  />
                </div>
                {/* Pincode Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Pincode</label>
                  <input
                    type="text"
                    value={profile.address.pincode}
                    onChange={e => handleAddressChange('pincode', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter pincode"
                    maxLength={6}
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Website</label>
              <div className="relative">
                <Globe className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
                <input
                  type="url"
                  value={profile.website}
                  onChange={(e) => handleInputChange('website', e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="https://yourwebsite.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Contact Number</label>
              <div className="relative">
                <Phone className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
                <input
                  type="tel"
                  value={profile.contactNumber}
                  onChange={(e) => handleInputChange('contactNumber', e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="+91 9876543210"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  value={profile.email}
                  disabled
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">About</label>
            <div className="relative">
              <textarea
                value={profile.about}
                onChange={(e) => handleInputChange('about', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                rows={6}
                maxLength={500}
                placeholder="Tell brands about yourself, your content style, and what makes you unique..."
              />
              <div className="absolute bottom-2 right-2 text-xs text-gray-500">
                {profile.about.length}/500
              </div>
            </div>
          </div>
        </div>

        {/* Interests */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Interests</h3>
          
          {/* Loading State */}
          {interestsLoading && (
            <div className="text-center py-4 text-gray-500">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
              Loading interests...
            </div>
          )}

          {/* Error State */}
          {interestsError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {interestsError}
            </div>
          )}

          {/* Interest Selection UI - Only show if not loading and no error */}
          {!interestsLoading && !interestsError && (
            <>
              {/* Category Filters */}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setSelectedCategory('')}
                  className={`px-3 py-1 text-sm rounded-full transition-colors ${
                    selectedCategory === '' 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  All Categories
                </button>
                {categories.map(category => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-3 py-1 text-sm rounded-full transition-colors ${
                      selectedCategory === category 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>

          <div className="relative">
            <Search className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={interestSearch}
              onChange={(e) => setInterestSearch(e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Search interests..."
            />
          </div>

          {(interestSearch || selectedCategory) && filteredInterests.length > 0 && (
            <div className="border border-gray-200 rounded-lg p-4 max-h-60 overflow-y-auto">
              {Object.entries(
                filteredInterests.reduce((acc, interest) => {
                  if (!acc[interest.category]) acc[interest.category] = [];
                  acc[interest.category].push(interest);
                  return acc;
                }, {})
              ).map(([category, categoryInterests]) => (
                <div key={category} className="mb-3 last:mb-0">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                    <span className="px-2 py-1 bg-gray-100 rounded text-xs mr-2">{category}</span>
                    <span className="text-gray-500 text-xs">({categoryInterests.length} items)</span>
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {categoryInterests.map(interest => (
                      <button
                        key={interest.id}
                        onClick={() => addInterest(interest)}
                        className="px-3 py-1.5 text-sm bg-white border border-gray-200 hover:bg-blue-50 hover:border-blue-300 text-gray-700 rounded-lg transition-colors"
                      >
                        {interest.name}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {(interestSearch || selectedCategory) && filteredInterests.length === 0 && (
            <div className="border border-gray-200 rounded-lg p-4 text-center text-gray-500 text-sm">
              No interests found. Try a different search or category.
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {selectedInterests.map(interest => {
              // Find the interest object to get its category
              const interestObj = interests.find(i => i.name === interest);
              const categoryTag = interestObj ? interestObj.category : '';

              return (
                <span
                  key={interest}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                >
                  {categoryTag && (
                    <span className="text-xs font-semibold mr-1 px-1.5 py-0.5 bg-blue-200 rounded">
                      {categoryTag}
                    </span>
                  )}
                  {interest}
                  <button
                    onClick={() => removeInterest(interest)}
                    className="ml-2 hover:text-blue-600"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              );
            })}
          </div>
            </>
          )}
        </div>

        {/* Privacy */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Privacy</h3>
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="privateProfile"
              checked={profile.isPrivate}
              onChange={(e) => handleInputChange('isPrivate', e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="privateProfile" className="text-sm text-gray-700">
              Private profile (only visible to connected brands)
            </label>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between pt-6 border-t border-gray-200">
          {message && (
            <div className={`text-sm ${message.includes('Error') ? 'text-red-600' : 'text-green-600'}`}>
              {message}
            </div>
          )}
          <div className="flex space-x-3 ml-auto">
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
            >
              <Save className="w-4 h-4" />
              <span>{loading ? 'Saving...' : 'Save Profile'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedProfileManagement;

